import { PageHeader, PageShell } from '@/components/PageShell';
import { JsonViewer } from '@/components/JsonViewer';
import { InfoBadge } from '@/components/badges/Common';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePromoteCandidate } from '@/db/usePromoteCandidate';
import { allEquipmentTypesState, customsForCandidateState, equipmentTypeCandidateState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import {
    type CustomEquipmentType,
    type EquipmentType,
    type EquipmentTypeCandidate,
    type EquipmentTypeCandidateId,
    type EquipmentTypeId,
    isOfferableType,
    type RemapEquipmentTypeResponse,
} from '@rock/types';
import { type FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatRemapSummary, mergeRemapResults, remapEquipmentType } from '../equipment/remapApi';
import { RemapTypeDialog } from '../equipment/RemapTypeDialog';
import EquipmentTypeForm from '../itemType/EquipmentTypeForm';
import { PromotionCard } from './PromotionCard';

const CandidateDetailsPage: FC = () => {
    const { candidateId: rawId } = useParams<{ candidateId: string }>();
    const candidateId = useMemo(
        () => (rawId ? (decodeURIComponent(rawId) as EquipmentTypeCandidateId) : ('' as EquipmentTypeCandidateId)),
        [rawId],
    );

    const [candidate, candidateLoadable] = useLoadableAtomValue(equipmentTypeCandidateState(candidateId));
    const [customs] = useLoadableAtomValue(customsForCandidateState(candidateId), []);
    const [liveTypes] = useLoadableAtomValue(allEquipmentTypesState, []);
    const isLoading = candidateLoadable.state === 'loading';

    const promotion = usePromoteCandidate(candidateId);
    const [promoting, setPromoting] = useState(false);
    const [linking, setLinking] = useState(false);
    const [remapTarget, setRemapTarget] = useState<CustomEquipmentType | undefined>();
    const [remapBusy, setRemapBusy] = useState(false);
    const [remapProgress, setRemapProgress] = useState<string | undefined>();
    // The customs list is a one-shot fetch; overlay this session's remaps so rows update in place.
    const [remappedTo, setRemappedTo] = useState<Record<string, EquipmentTypeId>>({});

    /** Where each custom ended up: this session's remaps overlaid on the fetched docs. */
    const replacedBy = useMemo(
        () => new Map(customs.map((custom) => [custom.id, remappedTo[custom.id] ?? custom.replacedBy])),
        [customs, remappedTo],
    );
    const unmappedCustoms = useMemo(
        () => customs.filter((custom) => !replacedBy.get(custom.id)),
        [customs, replacedBy],
    );

    // The remap callable resolves its target from the LIVE catalog, so a draft-only promotion type is
    // no pre-selection at all — offering it would pre-load a guaranteed not-found. (Remap all doesn't
    // need this guard: its button only renders once the card sees the type published.)
    const livePromotionTypeId = liveTypes.some(
        (type) => type.id === candidate?.promotionTypeId && isOfferableType(type),
    )
        ? candidate?.promotionTypeId
        : undefined;

    // Promotion = seeding the EXISTING draft → preview → publish pipeline from the candidate, and
    // pointing the candidate at what it seeded; nothing publishes from here. Once that type is live
    // (published from its own page), the Remap actions below consume the demand onto it.
    const handlePromoteToNewType = async (type: EquipmentType) => {
        if (await promotion.promoteToNewType(type)) setPromoting(false);
    };

    const handleLinkExistingType = async (typeId: EquipmentTypeId) => {
        if (await promotion.linkExistingType(typeId)) setLinking(false);
    };

    const handleRemap = async (custom: CustomEquipmentType, targetTypeId: EquipmentTypeId) => {
        setRemapBusy(true);
        try {
            const result = await remapOne(custom, targetTypeId);
            if (result) {
                toast.success(formatRemapSummary(result));
                setRemapTarget(undefined);
            }
        } finally {
            setRemapBusy(false);
        }
    };

    /**
     * Walk every custom still on this candidate onto the promoted type, one call at a time — the
     * server does real work per home and each call is fix-forward idempotent, so stopping at the first
     * failure leaves a resumable state rather than a half-applied fan-out.
     */
    const handleRemapAll = async (targetTypeId: EquipmentTypeId) => {
        const targets = unmappedCustoms;
        setRemapBusy(true);
        try {
            const results: RemapEquipmentTypeResponse[] = [];
            for (const [index, custom] of targets.entries()) {
                setRemapProgress(`Remapping ${index + 1} of ${targets.length}…`);
                const result = await remapOne(custom, targetTypeId);
                if (!result) break;
                results.push(result);
            }
            if (results.length > 0) {
                toast.success(`Remapped ${results.length} of ${targets.length} custom(s)`, {
                    description: formatRemapSummary(mergeRemapResults(results)),
                });
            }
        } finally {
            setRemapProgress(undefined);
            setRemapBusy(false);
        }
    };

    /** One remap call, with the row overlay updated on success. Toasting is the caller's job — a
     * declaration, not a const, so it can sit below the two handlers that call it. */
    async function remapOne(
        custom: CustomEquipmentType,
        targetTypeId: EquipmentTypeId,
    ): Promise<RemapEquipmentTypeResponse | undefined> {
        try {
            const result = await remapEquipmentType({
                homeId: custom.homeId,
                customTypeId: custom.id,
                targetTypeId,
            });
            setRemappedTo((prev) => ({ ...prev, [custom.id]: targetTypeId }));
            return result;
        } catch (error) {
            toast.error(`Remap failed for ${custom.title}`, {
                description: error instanceof Error ? error.message : String(error),
            });
            return undefined;
        }
    }

    return (
        <PageShell width="medium">
            <PageHeader title={candidate?.title ?? 'Candidate'} id={candidateId} backLabel="Back to candidates" />

            {isLoading ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Loading candidate...</CardTitle>
                    </CardHeader>
                </Card>
            ) : !candidate ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Candidate not found</CardTitle>
                        <CardDescription>It may have been removed.</CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <PromotionCard
                        candidate={candidate}
                        unmappedCount={unmappedCustoms.length}
                        busy={promotion.busy || remapBusy}
                        remapProgress={remapProgress}
                        onPromoteNew={() => setPromoting(true)}
                        onLinkExisting={() => setLinking(true)}
                        onUnlink={promotion.unlinkType}
                        onRemapAll={() => {
                            if (candidate.promotionTypeId) handleRemapAll(candidate.promotionTypeId);
                        }}
                    />
                    <Card>
                        <CardHeader>
                            <CardTitle>Overview</CardTitle>
                            <CardDescription>What homes have asked for and how it's categorized.</CardDescription>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                            <Field label="Space">
                                <InfoBadge>{candidate.spaceId}</InfoBadge>
                            </Field>
                            <Field label="Group">{candidate.group}</Field>
                            <Field label="Scope">{candidate.scope}</Field>
                            <Field label="Icon">{candidate.icon ?? '—'}</Field>
                            <Field label="AI Classification">{candidate.aiClassification}</Field>
                            <Field label="Homes">{candidate.count}</Field>
                            <Field label="First Seen">{formatDateTime(candidate.firstSeenAt)}</Field>
                            <Field label="Last Seen">{formatDateTime(candidate.lastSeenAt)}</Field>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>User Descriptions</CardTitle>
                            <CardDescription>What people typed when adding this item.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {candidate.sampleDescriptions.length > 0 ? (
                                <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                                    {candidate.sampleDescriptions.map((desc, i) => (
                                        <li key={`${desc}-${i}`}>{desc}</li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="section-description">No descriptions recorded.</div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Linked Custom Types</CardTitle>
                            <CardDescription>
                                {customs.length === 0
                                    ? 'No linked customs found'
                                    : unmappedCustoms.length === 0
                                      ? `${customs.length} home(s) tracked this — all remapped onto a catalog type`
                                      : `${customs.length} home(s) track this — ${unmappedCustoms.length} still to remap onto a live catalog type`}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {customs.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Home</TableHead>
                                            <TableHead>Title</TableHead>
                                            <TableHead>Type Id</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {customs.map((custom) => {
                                            const target = replacedBy.get(custom.id);
                                            return (
                                                <TableRow key={custom.id}>
                                                    <TableCell className="text-muted-foreground">
                                                        {custom.homeId}
                                                    </TableCell>
                                                    <TableCell className="font-medium">{custom.title}</TableCell>
                                                    <TableCell className="text-muted-foreground">{custom.id}</TableCell>
                                                    <TableCell>
                                                        {target ? (
                                                            <InfoBadge>→ {target}</InfoBadge>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => setRemapTarget(custom)}
                                                                disabled={remapBusy}
                                                            >
                                                                Remap…
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            ) : null}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Data</CardTitle>
                            <CardDescription>The raw candidate document stored in Firestore.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-[600px] overflow-auto rounded-md bg-muted p-4">
                                <JsonViewer data={candidate} />
                            </div>
                        </CardContent>
                    </Card>

                    {promoting && (
                        <Dialog open onOpenChange={() => setPromoting(false)}>
                            <DialogContent className="max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Promote to Equipment Type</DialogTitle>
                                    <DialogDescription className="m-2">
                                        Seeds a catalog-type draft from this candidate — review everything, pick an id,
                                        then publish from the type page and remap the linked customs onto it.
                                    </DialogDescription>
                                </DialogHeader>
                                <EquipmentTypeForm
                                    spaceId={candidate.spaceId}
                                    loading={promotion.busy}
                                    type={null}
                                    seed={promotionSeed(candidate)}
                                    onSave={handlePromoteToNewType}
                                    onDelete={() => setPromoting(false)}
                                    onCancel={() => setPromoting(false)}
                                />
                            </DialogContent>
                        </Dialog>
                    )}
                    <RemapTypeDialog
                        open={linking}
                        onOpenChange={setLinking}
                        title="Link to an existing type"
                        description="Points this candidate at a type the catalog already has. Nothing moves yet — remap the linked customs onto it from here afterwards."
                        spaceId={candidate.spaceId}
                        initialQuery={candidate.title}
                        confirmLabel="Link"
                        busyLabel="Linking…"
                        selectionLabel="Link to"
                        busy={promotion.busy}
                        onConfirm={handleLinkExistingType}
                    />
                    {remapTarget && (
                        <RemapTypeDialog
                            open
                            onOpenChange={(open) => !open && setRemapTarget(undefined)}
                            title={`Remap ${remapTarget.title}`}
                            description={`Home ${remapTarget.homeId}'s equipment moves onto the chosen catalog type; its models move with it, ids and manuals unchanged.`}
                            spaceId={remapTarget.spaceId}
                            initialQuery={remapTarget.title}
                            initialSelection={livePromotionTypeId}
                            busy={remapBusy}
                            onConfirm={(targetTypeId) => handleRemap(remapTarget, targetTypeId)}
                        />
                    )}
                </>
            )}
        </PageShell>
    );
};

/** The candidate's promotion-ready fields, as NEW-type form defaults (id stays the admin's call). */
function promotionSeed(candidate: EquipmentTypeCandidate): Partial<EquipmentType> {
    return {
        spaceId: candidate.spaceId,
        title: candidate.title,
        shortTitle: candidate.shortTitle,
        subtitle: candidate.subtitle,
        icon: candidate.icon,
        group: candidate.group,
        scope: candidate.scope,
    };
}

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <span>{children}</span>
    </div>
);

export default CandidateDetailsPage;
